import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { motion } from 'framer-motion';
import Image from '../../../components/AppImage';


const HeroSection = ({ onApplyNow, onCheckEligibility, currentLanguage = 'EN' }) => {
  const [isCardFlipped, setIsCardFlipped] = useState(false);
  const [creditScore, setCreditScore] = useState(0);

  const content = {
    EN: {
      preTitle: "Take Control of Your Credit",
      headline: "Build Credit for Life",
      subheadline: "Start your journey to better credit with our secured card designed for success. No credit history? No problem.",
      description: "Join thousands of Canadians who\'ve improved their credit score with our easy-to-use secured credit card.",
      applyNow: "Apply Now",
      checkEligibility: "Check Eligibility",
      creditScore: "Credit Score",
      trustBadge: "Trusted by 50,000+ Canadians"
    },
    FR: {
      preTitle: "Prenez le Contrôle de Votre Crédit",
      headline: "Construisez votre Crédit à Vie",
      subheadline: "Commencez votre parcours vers un meilleur crédit avec notre carte sécurisée conçue pour le succès. Pas d\'historique de crédit? Aucun problème.",
      description: "Rejoignez des milliers de Canadiens qui ont amélioré leur cote de crédit avec notre carte de crédit sécurisée facile à utiliser.",
      applyNow: "Faire une demande",
      checkEligibility: "Vérifiez votre admissibilité",
      creditScore: "Score de crédit",
      trustBadge: "Approuvé par 50 000+ Canadiens"
    }
  };

  const text = content?.[currentLanguage] || content?.EN;

  // Animate credit score on mount - Koho-style smooth animation
  useEffect(() => {
    const timer = setTimeout(() => {
      let currentScore = 0;
      const targetScore = 750;
      const increment = targetScore / 60; // 60 frames for smooth animation
      const interval = setInterval(() => {
        currentScore += increment;
        setCreditScore(Math.round(currentScore));
        if (currentScore >= targetScore) {
          clearInterval(interval);
          setCreditScore(targetScore);
        }
      }, 30); // 30ms intervals for 60fps
      return () => clearInterval(interval);
    }, 800);

    return () => clearTimeout(timer);
  }, []);

  // Auto-flip card every 6 seconds (longer for better UX)
  useEffect(() => {
    const interval = setInterval(() => {
      setIsCardFlipped(prev => !prev);
    }, 6000);

    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-[100vh] flex items-center justify-center bg-gradient-to-br from-background via-primary/5 to-accent/10 overflow-hidden">
      {/* Koho-style background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-primary/20 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
        <div className="absolute top-1/3 right-1/4 w-72 h-72 bg-accent/20 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-1/4 left-1/3 w-72 h-72 bg-secondary/10 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Content Section - Koho-inspired layout */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="space-y-8"
          >
            {/* Pre-title badge - Koho style */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-flex items-center px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium koho-shadow"
            >
              <span className="w-2 h-2 bg-primary rounded-full mr-2 animate-pulse"></span>
              {text?.preTitle}
            </motion.div>

            {/* Main title - Koho typography */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-4xl sm:text-5xl lg:text-7xl font-bold text-foreground leading-tight tracking-tight"
            >
              {text?.headline}
            </motion.h1>

            {/* Subtitle - Koho style */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-xl text-muted-foreground leading-relaxed max-w-xl"
            >
              {text?.subheadline}
            </motion.p>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="text-lg text-text-secondary leading-relaxed max-w-lg"
            >
              {text?.description}
            </motion.p>

            {/* CTA Buttons - Koho style */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Button
                variant="default"
                size="lg"
                onClick={onApplyNow}
                className="koho-gradient hover:scale-105 koho-shadow-lg font-semibold px-8 py-4 text-lg transition-all duration-300 border-0"
                iconName="ArrowRight"
                iconPosition="right"
              >
                {text?.applyNow}
              </Button>
              
              <Button
                variant="outline"
                size="lg"
                onClick={onCheckEligibility}
                className="bg-card hover:bg-primary/5 text-primary border-2 border-primary/20 hover:border-primary/40 font-semibold px-8 py-4 text-lg transition-all duration-300 hover:scale-105 koho-shadow"
                iconName="CheckCircle"
                iconPosition="left"
              >
                {text?.checkEligibility}
              </Button>
            </motion.div>

            {/* Trust badge - Koho style */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="flex items-center space-x-3 text-sm text-muted-foreground bg-card/50 koho-blur rounded-xl p-4 border border-border/50"
            >
              <div className="flex space-x-1">
                {[...Array(5)]?.map((_, i) => (
                  <motion.svg 
                    key={i}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, delay: 0.8 + i * 0.1 }}
                    className="w-4 h-4 text-primary" 
                    fill="currentColor" 
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </motion.svg>
                ))}
              </div>
              <span className="font-medium">{text?.trustBadge}</span>
            </motion.div>
          </motion.div>

          {/* Visual Section - Enhanced card animation */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative flex justify-center lg:justify-end"
          >
            <div className="relative w-80 h-80 lg:w-96 lg:h-96">
              {/* Enhanced 3D Card with Koho styling */}
              <motion.div
                className="relative w-full h-full cursor-pointer preserve-3d"
                animate={{ rotateY: isCardFlipped ? 180 : 0 }}
                transition={{ duration: 1.2, ease: "easeInOut" }}
                whileHover={{ scale: 1.05 }}
                onClick={() => setIsCardFlipped(!isCardFlipped)}
              >
                {/* Card front - Koho inspired design */}
                <div className="absolute inset-0 w-full h-full koho-gradient rounded-3xl koho-shadow-lg backface-hidden flex flex-col justify-between p-8 text-white">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="text-sm font-medium opacity-90">Credit Builder</div>
                      <div className="text-xs opacity-70 mt-1">Secured Card</div>
                    </div>
                    <div className="bg-white/20 rounded-xl p-3 backdrop-blur-sm">
                      <Icon name="CreditCard" size={24} className="text-white" />
                    </div>
                  </div>
                  
                  {/* Chip */}
                  <div className="w-12 h-8 bg-white/30 rounded-md backdrop-blur-sm"></div>
                  
                  <div className="space-y-4">
                    <div className="font-mono text-xl tracking-wider">
                      •••• •••• •••• 1234
                    </div>
                    <div className="flex justify-between items-end">
                      <div>
                        <div className="text-xs opacity-70">CARDHOLDER NAME</div>
                        <div className="text-sm font-medium">YOUR NAME</div>
                      </div>
                      <div>
                        <div className="text-xs opacity-70">VALID THRU</div>
                        <div className="text-sm font-medium">12/28</div>
                      </div>
                      {/* Updated Mastercard logo with provided asset */}
                      <div className="w-14 h-9 overflow-hidden rounded">
                        <Image
                          src="/assets/images/image-1757454378302.png"
                          alt="Mastercard"
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card back - Credit Score Display */}
                <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-secondary to-secondary/80 rounded-3xl koho-shadow-lg backface-hidden rotate-y-180 flex flex-col justify-center items-center p-8 text-white">
                  <div className="text-center mb-6">
                    <div className="text-lg font-semibold mb-2">{text?.creditScore}</div>
                  </div>
                  
                  {/* Enhanced Credit Score Circle */}
                  <div className="relative w-40 h-40 mb-6">
                    <svg className="transform -rotate-90 w-40 h-40">
                      <circle
                        cx="80"
                        cy="80"
                        r="70"
                        stroke="rgba(255,255,255,0.2)"
                        strokeWidth="8"
                        fill="transparent"
                      />
                      <motion.circle
                        cx="80"
                        cy="80"
                        r="70"
                        stroke="url(#scoreGradient)"
                        strokeWidth="8"
                        fill="transparent"
                        strokeLinecap="round"
                        strokeDasharray={439.82}
                        initial={{ strokeDashoffset: 439.82 }}
                        animate={{ strokeDashoffset: 439.82 - (creditScore / 850) * 439.82 }}
                        transition={{ duration: 2.5, ease: "easeOut" }}
                      />
                      <defs>
                        <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#00D4AA" />
                          <stop offset="100%" stopColor="#00E6B8" />
                        </linearGradient>
                      </defs>
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <motion.div 
                          className="text-4xl font-bold"
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ duration: 0.8, delay: 1.5, type: "spring" }}
                        >
                          {creditScore}
                        </motion.div>
                        <div className="text-sm opacity-80">Good</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-sm text-center opacity-80 max-w-xs">
                    Track your credit journey with personalized insights
                  </div>
                </div>
              </motion.div>

              {/* Hover instruction - Koho style */}
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2 }}
                className="absolute -bottom-12 left-1/2 transform -translate-x-1/2 text-muted-foreground text-sm text-center bg-card/80 koho-blur px-4 py-2 rounded-full border border-border/50"
              >
                Click to flip
              </motion.div>

              {/* Enhanced floating elements - Koho style */}
              <motion.div
                animate={{ y: [-15, 15, -15] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute top-8 -right-8 w-16 h-16 koho-gradient rounded-2xl flex items-center justify-center koho-shadow-lg backdrop-blur-sm"
              >
                <Icon name="Shield" size={24} className="text-white" />
              </motion.div>

              <motion.div
                animate={{ y: [15, -15, 15] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 2 }}
                className="absolute bottom-8 -left-8 w-20 h-20 bg-success rounded-2xl flex items-center justify-center koho-shadow-lg"
              >
                <Icon name="TrendingUp" size={28} className="text-white" />
              </motion.div>

              <motion.div
                animate={{ 
                  rotate: [0, 360],
                  scale: [1, 1.1, 1]
                }}
                transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                className="absolute top-1/2 -left-12 w-12 h-12 bg-warning rounded-full flex items-center justify-center koho-shadow"
              >
                <span className="text-white font-bold text-lg">✓</span>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
      {/* Enhanced scroll indicator - Koho style */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.5 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 12, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="flex flex-col items-center space-y-2 text-muted-foreground"
        >
          <span className="text-sm font-medium">Scroll to explore</span>
          <div className="w-6 h-10 border-2 border-primary/30 rounded-full flex justify-center bg-card/50 koho-blur">
            <motion.div
              animate={{ y: [0, 16, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="w-1 h-3 koho-gradient rounded-full mt-2"
            ></motion.div>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroSection;